/**
 * PhotoLightbox — fullscreen photo viewer per UX §3.12.
 * Backdrop is bone w/ blur (NOT black). Photo centered, max-h-[90vh].
 * Esc closes. ArrowLeft/Right navigate. Focus on close button when open.
 */
import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { PhotoId } from '@shared/types';
import { Skeleton } from './ui/Skeleton';
import { usePhotoUrl } from '../lib/queries';
import { useActiveHouseholdId } from '../lib/household';
import { cn } from '../lib/cn';

export interface PhotoLightboxProps {
  photoId: PhotoId;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  caption?: string;
}

export function PhotoLightbox({
  photoId,
  onClose,
  onPrev,
  onNext,
  caption,
}: PhotoLightboxProps) {
  const householdId = useActiveHouseholdId();
  const q = usePhotoUrl(householdId, photoId);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onPrev, onNext]);

  return (
    <Dialog.Root open onOpenChange={(o) => (o ? null : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-[70]',
            'bg-bg/85 backdrop-blur-md',
            'data-[state=open]:animate-[fade-in_280ms_var(--ease-out-slow)]',
          )}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed inset-0 z-[70] flex flex-col items-center justify-center p-6',
            'focus:outline-none',
          )}
        >
          <div className="sr-only">
            <Dialog.Title>{caption ?? 'Photo'}</Dialog.Title>
          </div>
          <div className="absolute top-4 right-4">
            <Dialog.Close
              aria-label="Close"
              autoFocus
              className={cn(
                'p-2 text-ink-700 hover:text-ink-900',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
              )}
            >
              <X size={24} weight="regular" />
            </Dialog.Close>
          </div>
          {onPrev ? (
            <button
              type="button"
              onClick={onPrev}
              aria-label="Previous photo"
              className={cn(
                'absolute left-4 top-1/2 -translate-y-1/2 p-2 text-ink-700 hover:text-ink-900',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
              )}
            >
              <CaretLeft size={28} weight="regular" />
            </button>
          ) : null}
          {onNext ? (
            <button
              type="button"
              onClick={onNext}
              aria-label="Next photo"
              className={cn(
                'absolute right-4 top-1/2 -translate-y-1/2 p-2 text-ink-700 hover:text-ink-900',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
              )}
            >
              <CaretRight size={28} weight="regular" />
            </button>
          ) : null}
          <figure className="max-w-[90vw] max-h-[90vh] flex flex-col items-center">
            {q.isPending || !q.data ? (
              <Skeleton className="w-[60vw] h-[60vh] max-w-3xl" />
            ) : (
              <img
                src={q.data.url}
                alt={caption ?? ''}
                className="max-h-[80vh] max-w-[90vw] object-contain border border-ink-200 bg-bg"
              />
            )}
            {caption ? (
              <figcaption className="mt-4 font-serif italic text-sm text-ink-700">
                {caption}
              </figcaption>
            ) : null}
          </figure>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
